import { Component, ViewChild, computed, signal } from '@angular/core';
import { DocsShellComponent, NavGroup, NavPanel } from './docs-shell/docs-shell.component';
import { DocsGuideComponent, GuideTool } from './guide/docs-guide.component';
import { DocsArchitectureComponent } from './architecture/docs-architecture.component';
import {
  ARCH_GROUPS,
  FLAT_GROUPS,
  GUIDE_PANEL_GROUPS,
  GUIDE_SDTM_GROUPS,
  GUIDE_USDM_GROUPS,
} from './docs-groups';

/** Which sub-guide is expanded below the chooser, if any. */
type DocsView = 'guide' | 'architecture';

/**
 * Documentation hub for Clinical AI Studio (`/docs`).
 *
 * Everything lives on one page. The sidebar shows the shared onboarding as flat
 * links, then two accordions — User Guide and User Architecture — listing every
 * section. Selecting a card (or an accordion leaf) reveals that path's sections
 * below and, for the guide, the right tool. The scroll-spy sidebar lives in
 * {@link DocsShellComponent}.
 */
@Component({
  selector: 'app-docs2',
  standalone: true,
  imports: [DocsShellComponent, DocsGuideComponent, DocsArchitectureComponent],
  templateUrl: './docs2.component.html',
  styleUrl: './docs2.component.css',
})
export class Docs2Component {
  @ViewChild(DocsShellComponent) private shell?: DocsShellComponent;

  /** null until the reader opens User Guide or User Architecture. */
  readonly view = signal<DocsView | null>(null);

  /** Within the User Guide: which tool's sections are shown (null = still on "Choose a tool"). */
  readonly guideTool = signal<GuideTool | null>(null);

  readonly flatGroups: NavGroup[] = FLAT_GROUPS;
  readonly panels: NavPanel[] = [
    { id: 'guide', label: 'User Guide', groups: GUIDE_PANEL_GROUPS },
    { id: 'architecture', label: 'User Architecture', groups: ARCH_GROUPS },
  ];

  /** Which accordion panel's sections are currently rendered (drives scroll-spy + auto-expand). */
  readonly activePanel = computed<string | null>(() => this.view());

  private static readonly USDM_IDS = new Set(
    GUIDE_USDM_GROUPS.flatMap((g) => g.items.map((i) => i.id)),
  );
  private static readonly SDTM_IDS = new Set(
    GUIDE_SDTM_GROUPS.flatMap((g) => g.items.map((i) => i.id)),
  );

  /** First section of each path — where the cards scroll to. */
  private static readonly VIEW_ANCHOR: Record<DocsView, string> = {
    guide: 'tools',
    architecture: 'arch-overview',
  };

  /** First section of each tool inside the User Guide. */
  private static readonly TOOL_ANCHOR: Record<GuideTool, string> = {
    usdm: 'usdm',
    sdtm: 'sdtm-overview',
  };

  /** Card click on the chooser. */
  select(view: DocsView): void {
    this.guideTool.set(null);
    this.view.set(view);
    this.scrollAfterRender(Docs2Component.VIEW_ANCHOR[view], view);
  }

  /** Tool card click inside the User Guide. */
  selectTool(tool: GuideTool): void {
    this.view.set('guide');
    this.guideTool.set(tool);
    this.scrollAfterRender(Docs2Component.TOOL_ANCHOR[tool], 'guide');
  }

  /** Accordion leaf click in the sidebar — switch to the section's view/tool, then scroll to it. */
  onNavigate(target: { panel: string; id: string }): void {
    if (target.panel === 'architecture') {
      this.guideTool.set(null);
      this.view.set('architecture');
    } else {
      this.view.set('guide');
      this.guideTool.set(this.toolForSection(target.id));
    }
    this.scrollAfterRender(target.id, target.panel);
  }

  private toolForSection(id: string): GuideTool | null {
    if (Docs2Component.USDM_IDS.has(id)) return 'usdm';
    if (Docs2Component.SDTM_IDS.has(id)) return 'sdtm';
    return null; // 'tools' — the "Choose a tool" landing
  }

  /** Wait for the new sections to render, then scroll to one and map the sidebar to it. */
  private scrollAfterRender(id: string, panel?: string | null): void {
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.shell?.focusSection(id, panel);
    });
  }
}
